import { useState, useEffect, type ReactNode } from "react";
import { useAuthContext } from "../auth/AuthContext";
import { getPermissions, type Permissions } from "../auth/roles";
import { EmptyPageIcon, PageIcon } from "./ReportsPage";
import { listReports, type ReportSummary } from "../api/reportApi";
import { getTemplate } from "../report-engine/templates";
import { APP_CONFIG } from "../config/app.config";

const ALL_CARDS: { id: string; icon: ReactNode; label: string; desc: string; color: string; permKey: keyof Permissions; flagged?: boolean }[] = [
  { id:"reports",   icon:<EmptyPageIcon size={28}/>, label:"Rapports",   desc:"Générer des rapports de performance.", color:"#00CC00", permKey:"canAccessReports" },
  { id:"analytics", icon:"📊",                       label:"Suivi",      desc:"Suivi des sites en temps réel.", color:"#0078d4", permKey:"canAccessAnalytics" },
  { id:"data",      icon:"🗄️",                       label:"Data",       desc:"Consulter les données brutes des sites.", color:"#8a8a00", permKey:"canAccessData" },
  { id:"documents", icon:"📁",                       label:"Documents",  desc:"Accéder aux documents et fichiers partagés.",  color:"#6c11b0", permKey:"canAccessDocuments", flagged: !APP_CONFIG.sharepointEnabled },
  { id:"settings",  icon:"⚙️",                       label:"Paramètres", desc:"Configurer les préférences de l'application.", color:"#e67e22", permKey:"canAccessSettings" },
];

const dtf = new Intl.DateTimeFormat("fr-FR", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });

export default function DashboardPage({ onNavigate }: { onNavigate:(id:string)=>void }) {
  const { user, role } = useAuthContext();
  const perms = getPermissions(role);
  // Seules les sections accessibles apparaissent dans les cartes
  const cards = ALL_CARDS.filter(c => !c.flagged && perms[c.permKey] !== false);

  const [recent, setRecent] = useState<ReportSummary[]>([]);

  useEffect(() => {
    listReports()
      .then(list => setRecent(list.slice(0, 3)))
      .catch(() => { /* silencieux */ });
  }, []);

  const hour      = new Date().getHours();
  const greeting  = hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir";
  const firstName = user?.name.split(" ")[0] ?? "Utilisateur";

  return (
    <div className="screen">
      <div className="screen-inner">

        <div className="mb-32">
          <h1 className="page-title">{greeting}, {firstName} 👋</h1>
          <p className="page-subtitle">Que souhaitez-vous faire aujourd'hui ?</p>
        </div>

        {/* Cartes de navigation — filtrées par permissions */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:16 }}>
          {cards.map(card => (
            <button key={card.id} onClick={() => onNavigate(card.id)}
              className="dashboard-card">
              <div style={{ width:46, height:46, borderRadius:10, background:card.color+"18",
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, marginBottom:14 }}>
                {card.icon}
              </div>
              <div className="t-title" style={{ fontSize:15, color:"var(--text-primary)", marginBottom:6 }}>{card.label}</div>
              <div style={{ fontSize:12, color:"var(--text-muted)", lineHeight:1.5 }}>{card.desc}</div>
            </button>
          ))}
        </div>

        {/* Activité récente */}
        <div style={{ marginTop:40 }}>
          <h2 className="section-title">Activité récente</h2>

          {recent.length === 0 ? (
            <div className="card p-32" style={{ textAlign:"center", color:"var(--text-faint)" }}>
              <div style={{ fontSize:36, marginBottom:10 }}>📭</div>
              <p style={{ margin:0, fontSize:14 }}>Aucune activité récente.</p>
              <p style={{ margin:"6px 0 0", fontSize:12 }}>Créez votre premier rapport pour commencer.</p>
              {perms.canCreateReport && (
                <button onClick={() => onNavigate("reports")} className="btn btn-black mt-16">
                  Créer un rapport
                </button>
              )}
            </div>
          ) : (
            <div className="flex-col gap-8">
              {recent.map(s => {
                const label = [s.clientName, s.siteName].filter(Boolean).join(" — ") || s.siteId;
                return (
                  <div key={s.id} className="card"
                    style={{ padding:"14px 18px", display:"flex", alignItems:"center", gap:12 }}>
                    <PageIcon periodKey={s.periodKey} size={40}/>
                    <div style={{ flex:1, overflow:"hidden" }}>
                      <div className="t-title" style={{ fontSize:13, color:"var(--text-primary)",
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {label}
                      </div>
                      <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:2 }}>
                        {getTemplate(s.templateId)?.label ?? s.templateId} · {s.periodKey}
                        {s.updatedBy ? ` · ${s.updatedBy}` : ""}
                      </div>
                    </div>
                    <div style={{ fontSize:11, color:"var(--text-faint)", flexShrink:0 }}>
                      {dtf.format(new Date(s.updatedAt))}
                    </div>
                  </div>
                );
              })}
              <button onClick={() => onNavigate("reports")} className="btn btn-ghost btn-sm"
                style={{ alignSelf:"flex-start", marginTop:4 }}>
                Voir tous les rapports →
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
